export interface BalancePayment {
  amount: number;
  paymentType: 'receipt' | 'disbursement';
}

export interface BalanceSalesDoc {
  docType: 'invoice' | 'credit_note';
  netToPay: number;
}

export interface BalancePurchaseDoc {
  docType: 'invoice' | 'credit_note';
  netToPay: number;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * Client balance: positive = client has credit with us (overpaid or prepaid).
 *                 negative = client owes us.
 *
 * balance = (receipts from client) - (disbursements to client)
 *           - (invoices) + (credit notes)
 */
export function computeClientBalance(
  payments: BalancePayment[],
  documents: BalanceSalesDoc[],
): number {
  let paid = 0;
  let refunded = 0;
  for (const p of payments) {
    if (p.paymentType === 'receipt') paid += Number(p.amount) || 0;
    else if (p.paymentType === 'disbursement') refunded += Number(p.amount) || 0;
  }
  let invoicesAmount = 0;
  let creditNotesAmount = 0;
  for (const d of documents) {
    if (d.docType === 'invoice') invoicesAmount += Number(d.netToPay) || 0;
    else if (d.docType === 'credit_note') creditNotesAmount += Number(d.netToPay) || 0;
  }
  return round3(paid - refunded - invoicesAmount + creditNotesAmount);
}

/**
 * Supplier balance: positive = supplier has credit with us (we overpaid).
 *                   negative = we owe the supplier.
 *
 * balance = (disbursements to supplier) - (receipts from supplier)
 *           - (purchase invoices) + (supplier credit notes)
 */
export function computeSupplierBalance(
  payments: BalancePayment[],
  documents: BalancePurchaseDoc[],
): number {
  let paid = 0;
  let received = 0;
  for (const p of payments) {
    if (p.paymentType === 'disbursement') paid += Number(p.amount) || 0;
    else if (p.paymentType === 'receipt') received += Number(p.amount) || 0;
  }
  let invoicesAmount = 0;
  let creditNotesAmount = 0;
  for (const d of documents) {
    if (d.docType === 'invoice') invoicesAmount += Number(d.netToPay) || 0;
    else if (d.docType === 'credit_note') creditNotesAmount += Number(d.netToPay) || 0;
  }
  return round3(paid - received - invoicesAmount + creditNotesAmount);
}
